import { z } from "@reflag/shared/utils/zod";

import { APIEmptyResponse, APIResponse } from "./api";
import {
  FeatureTargeting,
  UpdateEnvironmentsFeatureTargetingSchema,
} from "./featureTargetingAPI";
import { nameSchema } from "./schemas/nameSchema";
import { StageHeaderSchema } from "./schemas/shared";

export const StageColors = [
  "red",
  "orange",
  "yellow",
  "green",
  "teal",
  "blue",
  "cyan",
  "purple",
  "pink",
] as const;

export const StageColorSchema = z.enum(StageColors).meta({
  description: "Stage color (HTML color name or hex code)",
  id: "stageColor",
});

export type StageHeaderDTO = z.output<typeof StageHeaderSchema>;
export type StageColor = z.infer<typeof StageColorSchema>;

// Post & Patch DTO

export const StagePostSchema = z
  .object({
    name: nameSchema.max(64),
    color: StageColorSchema.default("red"),
    order: z.number().optional(),
    stageEnvironments: UpdateEnvironmentsFeatureTargetingSchema,
  })
  .strict();

export type StagePostArgs = z.input<typeof StagePostSchema>;
export type StagePostArgsOutput = z.output<typeof StagePostSchema>;

export const StagePatchSchema = StagePostSchema.omit({ color: true })
  // Redefine to remove the default value allowing for partial updates
  .extend({ color: StageColorSchema })
  .partial()
  .strict();
export type StagePatchArgs = z.input<typeof StagePatchSchema>;

export const StageDeleteSchema = z.object({
  newStageId: z.string().optional(),
});

export type StageDeleteArgs = z.input<typeof StageDeleteSchema>;

// Model

export type StageEnvironmentDTO = FeatureTargeting & {
  id: string;
  environment: {
    id: string;
    isProduction: boolean;
    name: string;
    order: number;
  };
};

export type StageDTO = {
  id: string;
  name: string;
  color: string;
  order: number;
  featureCount: number;
  createdAt: string;
  stageEnvironments: StageEnvironmentDTO[];
};

export type StageNameDTO = Pick<StageDTO, "id" | "name" | "color" | "order">;

// Queries

export const StagesSortByColumns = ["name", "order", "createdAt"] as const;
const StagesQuerySortBySchema = z.enum(StagesSortByColumns);
export type StagesQuerySortBy = z.infer<typeof StagesQuerySortBySchema>;

export const StagesQuerySchema = z
  .object({
    sortOrder: z.enum(["asc", "desc"]).default("asc"),
    sortBy: StagesQuerySortBySchema.default("order"),
  })
  .strict();

export type StagesQuery = z.input<typeof StagesQuerySchema>;

// API

export interface StageAPI {
  "/apps/:appId/stages": {
    POST: {
      body: StagePostArgs;
      response: APIResponse<{
        stage: StageDTO;
      }>;
      params: { appId: string };
    };
    GET: {
      response: APIResponse<{
        stages: StageDTO[];
        sortBy: StagesQuerySortBy;
        sortOrder: "asc" | "desc";
      }>;
      params: { appId: string };
      query: StagesQuery;
    };
  };
  "/apps/:appId/stages/:stageId": {
    GET: {
      response: APIResponse<{
        stage: StageDTO;
      }>;
      params: { appId: string; stageId: string };
    };
    PATCH: {
      body: StagePatchArgs;
      response: APIResponse<{
        stage: StageDTO;
      }>;
      params: { appId: string; stageId: string };
    };
    DELETE: {
      body: StageDeleteArgs;
      response: APIEmptyResponse;
      params: { appId: string; stageId: string };
    };
  };
}
